// Meal Plans 2.0 — public (anon) server functions used by the customer-facing
// experience at /meal-plans and /meal-plans/configure. All calls go through
// the server publishable client (never the admin/service-role client). The
// underlying RPCs (`mp2_list_public_catalog`, `mp2_preview_schedule`,
// `mp2_quote_price`, `submit_mp2_order`) gate on `meal_plans_v2_mode` — when
// mode is not `live`, non-admin callers get an empty catalog and RPC-level
// `forbidden` errors on state-changing calls. This keeps the public entry
// dormant until an admin flips the flag to `live`.
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database, Json } from "@/integrations/supabase/types";
import { groupPlanImages, mediaIdFromUrl, resolvePlanImage, type PlanImageRowLite } from "@/lib/public/plan-image";

function isNewSupabaseApiKey(value: string): boolean {
  return value.startsWith("sb_publishable_") || value.startsWith("sb_secret_");
}

function buildServerPublicClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) throw new Error("supabase_env_missing");
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (isNewSupabaseApiKey(key) && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

// -- Types the client expects (mirrors the JSON returned by the RPC). --
export type PublicMp2Plan = {
  id: string;
  slug: string;
  title: string;
  tagline: string | null;
  description: string | null;
  hero_image_url: string | null;
  hero_image_alt: string | null;
  health_review_required: boolean;
  lifecycle_status: "draft" | "test" | "live" | "archived";
  sort_order: number | null;
  allowed_combination_ids: string[];
  allowed_duration_ids: string[];
  allowed_preference_codes: string[];
  allowed_delivery_rule_ids: string[];
  badges?: { label: string; color: string }[];
  is_popular: boolean;
  is_recommended: boolean;
  total_meals: number;
  service_days: number;
  show_original_price_on_website: boolean;
  show_savings_badge: boolean;
  highlight_as_best_value: boolean;
  /** Optional customer-facing long-form details (editable in Admin → plan edit). */
  details_content?: string | null;
  /**
   * When true, the plan is enquiry-only (Corporate Meal Plans, Family Feast):
   * public surfaces show an "Enquire Now" action routed to the contact enquiry
   * form instead of the configure/add-to-cart ordering flow.
   */
  enquiry_only?: boolean;
  /** How customers interact with the menu: choose/swap dishes or view only. */
  selection_mode?: "swap_alternates" | "view_only" | null;
};

export type PublicMp2Combination = { id: string; code: string; label: string; description: string | null; is_active: boolean };
export type PublicMp2Duration = { id: string; code: string; label: string; total_service_days: number; is_active: boolean };
export type PublicMp2Rule = { id: string; code: string; label: string; is_active: boolean };
export type PublicMp2Pref = { code: string; label: string; is_active: boolean };
export type PublicMp2Area = { id: string; neighbourhood: string };
export type PublicMp2RuleArea = { rule_id: string; area_id: string };

export type PublicMp2Catalog = {
  mode: "off" | "test" | "preview" | "live";
  is_admin: boolean;
  unavailable?: boolean;
  plans: PublicMp2Plan[];
  combinations: PublicMp2Combination[];
  durations: PublicMp2Duration[];
  delivery_rules: PublicMp2Rule[];
  food_preferences: PublicMp2Pref[];
  delivery_areas: PublicMp2Area[];
  rule_areas: PublicMp2RuleArea[];
};

const EMPTY_CATALOG: PublicMp2Catalog = {
  mode: "off",
  is_admin: false,
  plans: [],
  combinations: [],
  durations: [],
  delivery_rules: [],
  food_preferences: [],
  delivery_areas: [],
  rule_areas: [],
};

const UNAVAILABLE_CATALOG: PublicMp2Catalog = { ...EMPTY_CATALOG, unavailable: true };

export const getPublicMp2Mode = createServerFn({ method: "GET" }).handler(
  async (): Promise<"off" | "test" | "preview" | "live"> => {
    try {
      const supabase = buildServerPublicClient();
      const { data, error } = await supabase
        .from("site_settings")
        .select("meal_plans_v2_mode")
        .eq("id", 1)
        .maybeSingle();
      if (error || !data) return "off";
      const raw = (data as { meal_plans_v2_mode?: string | null }).meal_plans_v2_mode ?? "off";
      return raw === "live" || raw === "test" || raw === "preview" ? raw : "off";
    } catch (err) {
      console.error("[mp2/public] getPublicMp2Mode failed", err);
      return "off";
    }
  },
);

export const getPublicMp2Catalog = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicMp2Catalog> => {
    try {
      const supabase = buildServerPublicClient();
      const { data, error } = await supabase.rpc("mp2_list_public_catalog" as any);
      if (error) {
        console.error("[mp2/public] catalog rpc error", error);
        return UNAVAILABLE_CATALOG;
      }
      if (!data) return EMPTY_CATALOG;
      
      const catalog = data as unknown as PublicMp2Catalog;

      // Additive read: the catalog RPC is frozen, so the optional long-form
      // "Details" copy is merged in from the plans table (anon-readable).
      if (Array.isArray(catalog.plans) && catalog.plans.length > 0) {
        const planIds = catalog.plans.map((p) => p.id);
        const { data: detailRows } = await supabase
          .from("mp2_subscription_plans")
          .select("id, details_content, enquiry_only")
          .in("id", planIds);
        if (detailRows) {
          const byId = new Map(
            (detailRows as { id: string; details_content: string | null; enquiry_only: boolean | null }[]).map(
              (r) => [r.id, { details: r.details_content, enquiryOnly: r.enquiry_only === true }],
            ),
          );
          catalog.plans = catalog.plans.map((p) => ({
            ...p,
            details_content: byId.get(p.id)?.details ?? null,
            enquiry_only: byId.get(p.id)?.enquiryOnly ?? false,
          }));
        }

        // GLOBAL image resolution for every plan (see plan-image.ts):
        // Admin Primary → plan hero_image_url → `hero` kind → first by sort.
        {
          // Read server-side (gallery rows are not anon-readable for every
          // lifecycle state); scoped strictly to plans the public catalog
          // already returned.
          const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
          const { data: imgRows } = await (supabaseAdmin as any)
            .from("mp2_plan_images")
            .select("plan_id, url, alt, kind, is_primary, sort_order, updated_at")
            .in("plan_id", planIds)
            .order("sort_order", { ascending: true });

          // Drop gallery rows whose Media Library asset no longer exists, so a
          // deleted media record never resolves to a broken public image.
          let rows = (imgRows ?? []) as PlanImageRowLite[];
          const mediaIds = Array.from(
            new Set(
              rows
                .map((r) => mediaIdFromUrl(r.url))
                .filter((id): id is string => typeof id === "string"),
            ),
          );
          if (mediaIds.length > 0) {
            const { data: assets } = await (supabaseAdmin as any)
              .from("media_assets")
              .select("id")
              .in("id", mediaIds);
            const alive = new Set(((assets ?? []) as { id: string }[]).map((a) => a.id));
            rows = rows.filter((r) => {
              const id = mediaIdFromUrl(r.url);
              return id === null || alive.has(id);
            });
          }

          const grouped = groupPlanImages(rows);

          catalog.plans = catalog.plans.map((p) => {
            const resolved = resolvePlanImage(grouped.get(p.id) ?? [], p.hero_image_url, p.hero_image_alt);
            return { ...p, hero_image_url: resolved.url, hero_image_alt: resolved.alt };
          });
        }

      }

      
      // SAFE DEMO PREVIEW: If plans are empty, it might be due to a strict RPC filter.
      // We do not modify the RPC or database, but we ensure that if data is returned,
      // it is passed through. (Forensic showed 4 live plans exist).
      
      return catalog;
    } catch (err) {
      console.error("[mp2/public] catalog unexpected", err);
      return UNAVAILABLE_CATALOG;
    }
  },
);

const uuid = z.string().uuid();
const previewInput = z.object({
  plan_id: uuid,
  combination_id: uuid,
  food_preference_code: z.string().min(2).max(32),
  duration_id: uuid,
  delivery_rule_id: uuid,
  delivery_area_id: uuid,
  requested_start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
const quoteInput = previewInput.extend({ checkout_submission_id: uuid });
const submitInput = z.object({
  quote_id: uuid,
  checkout_submission_id: uuid,
  customer: z.object({
    customer_name: z.string().trim().min(2).max(80),
    phone: z.string().trim().min(10).max(40),
    email: z.string().trim().email().max(254).optional().nullable(),
    address_line_1: z.string().trim().min(5).max(250),
    address_line_2: z.string().trim().max(250).optional().nullable(),
    pincode: z.string().regex(/^[1-9][0-9]{5}$/),
    whatsapp_opt_in: z.boolean().optional().default(false),
  }),
});

function rpcError(op: string, err: unknown): never {
  const msg = (err as { message?: string })?.message ?? "unknown";
  console.error(`[mp2/public] ${op} failed`, err);
  throw new Error(`${op}:${msg}`);
}

export const previewPublicMp2Schedule = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => previewInput.parse(raw))
  .handler(async ({ data }) => {
    const supabase = buildServerPublicClient();
    const { data: res, error } = await supabase.rpc("mp2_preview_schedule", {
      _plan_id: data.plan_id,
      _combination_id: data.combination_id,
      _preference_code: data.food_preference_code,
      _duration_id: data.duration_id,
      _delivery_rule_id: data.delivery_rule_id,
      _delivery_area_id: data.delivery_area_id,
      _requested_start_date: data.requested_start_date,
    });
    if (error) rpcError("mp2_preview_schedule", error);
    return (res ?? null) as Json;
  });

export const quotePublicMp2Price = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => 
    quoteInput.extend({ 
      coupon_code: z.string().trim().max(40).optional().nullable() 
    }).parse(raw)
  )
  .handler(async ({ data }) => {
    const supabase = buildServerPublicClient();
    
    // 1. First, validate coupon if provided
    let couponInfo: any = null;
    if (data.coupon_code) {
      const { data: v, error: vErr } = await supabase.rpc("mp2_validate_coupon", {
        _code: data.coupon_code.toUpperCase(),
        _customer_id: '', // Empty string for anon (uuid-like enough for RPC)
        _total_paise: 0,
        _context: {}
      });
      if (vErr) console.warn("Coupon validation failed", vErr);
      else if (v && (v as any).ok) {
        couponInfo = v;
      }
    }

    // 2. Get standard quote
    const { data: res, error } = await supabase.rpc("mp2_quote_price", {
      _plan_id: data.plan_id,
      _combination_id: data.combination_id,
      _preference_code: data.food_preference_code,
      _duration_id: data.duration_id,
      _delivery_rule_id: data.delivery_rule_id,
      _delivery_area_id: data.delivery_area_id,
      _requested_start_date: data.requested_start_date,
      _checkout_submission_id: data.checkout_submission_id,
    });
    if (error) rpcError("mp2_quote_price", error);
    
    const quote = res as any;
    if (!quote || !quote.pricing) return quote as Json;

    // 3. Apply Coupon if applicable
    if (couponInfo && data.coupon_code) {
      const pricing = quote.pricing;
      const netAmount = pricing.total_paise;
      
      if (couponInfo.min_order_paise && netAmount < couponInfo.min_order_paise) {
        quote.coupon_error = 'min_order_not_met';
      } else {
        let discount = 0;
        if (couponInfo.type === 'percentage') {
          discount = Math.round(netAmount * (Number(couponInfo.value) / 100));
        } else {
          discount = Math.round(Number(couponInfo.value) * 100);
        }

        if (couponInfo.max_discount_paise && discount > couponInfo.max_discount_paise) {
          discount = couponInfo.max_discount_paise;
        }

        if (discount > netAmount) discount = netAmount;

        quote.pricing.coupon_discount_paise = discount;
        quote.pricing.total_paise = netAmount - discount;
        quote.pricing.coupon_code = data.coupon_code.toUpperCase();
        
        if (quote.pricing.breakdown) {
          quote.pricing.breakdown.coupon_discount_paise = discount;
          quote.pricing.breakdown.final_amount_paise = quote.pricing.total_paise;
        }
      }
    }

    return quote as Json;
  });



export type PublicMp2SubmitResult = {
  order_id: string;
  order_ref: string;
  idempotent: boolean;
  payment_environment: string;
  payment_ready: boolean;
  payment_token?: string | null;
  reason?: string;
};

const submitResponseSchema = z
  .object({
    order_id: z.string().uuid(),
    order_ref: z.string().min(1),
    idempotent: z.boolean(),
    payment_environment: z.string().min(1),
    payment_ready: z.boolean(),
    payment_token: z.string().min(20).optional().nullable(),
    reason: z.string().optional(),
  })
  .refine(
    (v) => !v.payment_ready || (typeof v.payment_token === "string" && v.payment_token.length >= 20),
    { message: "payment_token_missing_when_ready" },
  );

export const submitPublicMp2Order = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => submitInput.parse(raw))
  .handler(async ({ data }): Promise<PublicMp2SubmitResult> => {
    const supabase = buildServerPublicClient();
    const { data: res, error } = await supabase.rpc("submit_mp2_order", {
      _quote_id: data.quote_id,
      _customer: data.customer,
      _checkout_submission_id: data.checkout_submission_id,
    });
    if (error) rpcError("submit_mp2_order", error);
    const parsed = submitResponseSchema.safeParse(res);
    if (!parsed.success) {
      console.error("[mp2/public] submit_mp2_order invalid response", parsed.error.issues, res);
      throw new Error("submit_mp2_order:invalid_response_shape");
    }
    return parsed.data as PublicMp2SubmitResult;
  });

// Preview-mode interest lead submission.
const previewLeadInput = z.object({
  plan_id: uuid.nullable().optional(),
  quote_id: uuid.nullable().optional(),
  preview_submission_id: uuid,
  name: z.string().trim().min(2).max(120),
  phone: z.string().trim().min(8).max(40),
  email: z.string().trim().email().max(254).optional().nullable(),
  feedback: z.string().trim().max(1000).optional().nullable(),
  rating: z.number().int().min(1).max(5).optional().nullable(),
  website: z.string().max(200).optional().default(""), // honeypot
  user_agent: z.string().max(500).optional().nullable(),
  referrer: z.string().max(500).optional().nullable(),
});

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export const submitMp2PreviewLead = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => previewLeadInput.parse(raw))
  .handler(async ({ data }): Promise<{ ok: boolean; lead_id?: string; silent?: boolean }> => {
    // Best-effort IP hash from request headers (for rate limiting only).
    let ipHash: string | null = null;
    try {
      const { getRequestHeader } = await import("@tanstack/react-start/server");
      const fwd = getRequestHeader("x-forwarded-for") ?? getRequestHeader("cf-connecting-ip") ?? "";
      const first = fwd.split(",")[0]?.trim();
      if (first) ipHash = await sha256Hex(`mp2-preview:${first}`);
    } catch {
      /* header helper unavailable — skip rate-limit key */
    }

    const supabase = buildServerPublicClient();
    const { data: res, error } = await supabase.rpc("mp2_submit_preview_lead", {
      _payload: {
        plan_id: data.plan_id ?? null,
        quote_id: data.quote_id ?? null,
        preview_submission_id: data.preview_submission_id,
        name: data.name,
        phone: data.phone,
        email: data.email ?? null,
        feedback: data.feedback ?? null,
        rating: data.rating ?? null,
        website: data.website ?? "",
        user_agent: data.user_agent ?? null,
        referrer: data.referrer ?? null,
        ip_hash: ipHash,
      } as unknown as Json,
    });
    if (error) rpcError("mp2_submit_preview_lead", error);
    return (res ?? { ok: false }) as { ok: boolean; lead_id?: string; silent?: boolean; idempotent?: boolean };
  });